import React, {
    useEffect,
    useMemo,
    useState,
} from 'react';
import {
    Flex,
    FlexProps,
    Grid,
    Text,
} from '@chakra-ui/react';
import {
    BlockHeader,
    GridCard,
    ImageModel,
    useAppDispath,
} from 'shared';
import { useSearchParams } from 'react-router-dom';
// eslint-disable-next-line no-restricted-imports
import { Tabs } from 'shared/ui/Tabs';
import { CulinaryStudioArticleCardModel, CulinaryStudioArticleCategoryModel } from 'entities';
import { LeadSmallSection } from 'features';
import { Helmet } from 'react-helmet-async';
// eslint-disable-next-line no-restricted-imports
import { richTextToString } from 'shared/lib/richtext-to-string';
import {
    CulinaryStudioPageActions,
    useCulinaryStudioPageState,
} from '../../store';
/**
 * Функция, которая создаёт компонент лейбла для таба
 *
 * @param {string} label Текст
 * @returns {JSX.Element} Компонент
 */
const createCulinaryStudioArticlesTabsItemLabel = (
    label: string,
) => (
    <Text fontSize="1.5rem">{label.toLocaleUpperCase()}</Text>
);

/**
 * Функция, которая создаёт компонент контента в табе
 *
 * @param {CulinaryStudioArticleModel[]} culinaryStudioArticles Список позиций кондитерской
 * @returns {JSX.Element} Компонент
 */
const createCulinaryStudioArticlesTabsContentItem = (
    culinaryStudioArticles: CulinaryStudioArticleCardModel[],
) => (
    <Grid
        key="all"
        templateColumns={{
            base: '1fr 1fr',
            lg: '1fr 1fr 1fr 1fr',
        }}
        gap="2rem"
    >
        {culinaryStudioArticles.map(({
            uid,
            name: name2,
            image,

        }) => (
            <GridCard
                label={name2}
                to={`/culinary-studio/${uid}`}
                src={image?.url}
                aspectRatio="2 / 3"
            />
        ))}
    </Grid>
);

/**
 * Функция, которая создаёт список компонентов для табов
 *
 * @param {CulinaryStudioArticleModel[]} culinaryStudioArticles Список позиций кондитерской
 * @param {CulinaryStudioArticleCategoryTagModel[]} sweetShopArticleCategoryTags
 * Список тегов для текущей категории
 * @returns {JSX.Element} Параметр items в TabsProps
 */
const createCulinaryStudioArticlesTabsItems = (
    culinaryStudioArticles: CulinaryStudioArticleCardModel[],
    culinaryStudioArticleCategory: CulinaryStudioArticleCategoryModel[],
) => culinaryStudioArticleCategory.map(({
    uid,
    name,
}) => ({
    key: uid,
    labelComponent: createCulinaryStudioArticlesTabsItemLabel(name),
    contentComponent: createCulinaryStudioArticlesTabsContentItem(culinaryStudioArticles),
}));

export type CulinaryStudioPageMainProps = {
    title: string,
    description?: any,
    image: ImageModel,
} & FlexProps & React.RefAttributes<HTMLDivElement>

export const CulinaryStudioPageMain: React.FC<CulinaryStudioPageMainProps> = ({
    title,
    description,
    image,
    ...props
}) => {
    const {
        culinaryStudioArticleCategories,
        culinaryStudioArticleCards,
        leadFormPdf,
    } = useCulinaryStudioPageState();
    const dispatch = useAppDispath();
    const [searchParams, setSearchParams] = useSearchParams();

    const [selectedTabKey, setSelectedTabKey] = useState(searchParams.get('tab') || undefined);

    useEffect(() => {
        const urlTab = searchParams.get('tab');
        if (urlTab && urlTab !== selectedTabKey) {
            setSelectedTabKey(urlTab);
        }
    }, [searchParams.get('tab')]);

    useEffect(() => {
        if (!selectedTabKey) {
            setSelectedTabKey(culinaryStudioArticleCategories.at(0)?.uid);
        }
    }, [culinaryStudioArticleCategories]);

    useEffect(() => {
        if (selectedTabKey) {
            dispatch(CulinaryStudioPageActions.getCulinaryStudioArticleCardsByCategoryUid({
                categoryUid: selectedTabKey,
            }));
            if (selectedTabKey !== searchParams.get('tab')) {
                setSearchParams({
                    tab: selectedTabKey,
                });
            }
        }
    }, [selectedTabKey]);

    /**
     * Мемоизированные табы
     */
    const memoTabsItems = useMemo(() => (
        createCulinaryStudioArticlesTabsItems(
            culinaryStudioArticleCards,
            culinaryStudioArticleCategories,
        )
    ), [culinaryStudioArticleCards, culinaryStudioArticleCategories]);

    return (
        <Flex
            {...props}
            flexDirection="column"
        >
            <Helmet>
                <title>{title}</title>
                <meta name="description" content={richTextToString(description)} />
                <meta property="og:title" content={title} />
            </Helmet>
            <Flex>
                <BlockHeader
                    title={title}
                    imageSrc={image.url}
                    description={description}
                    width={{
                        base: '100vw',
                        sm: '100%',
                    }}
                />
            </Flex>
            <Grid
                background="#000000"
            >
                <LeadSmallSection
                    title="НЕ МОЖЕТЕ ОПРЕДЕЛИТЬСЯ?"
                    padding="2rem"
                    buttonText="Заказать звонок"
                    background="#000000"
                    maxWidth={{
                        base: '425px',
                        lg: '1280px',
                    }}
                    width="100%"
                    margin="0 auto"
                    pdfUrl={leadFormPdf?.url}
                />
            </Grid>
            <Tabs
                value={selectedTabKey}
                onValueChange={(v) => setSelectedTabKey(v)}
                items={memoTabsItems}
                maxWidth={{
                    base: '425px',
                    lg: '1280px',
                }}
                background="white"
                width="100%"
                margin="0 auto"
                padding="2rem"
            />
        </Flex>
    );
};
